import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import createMutationRequest from 'utils/createMutationRequest';


import useAPI from '../../../services/useAPI';
import { useTranslation } from '../../../localization/useTranslation';
import SimpleTable, { createDefaultActions, SortDirection } from '../../Common/SimpleTable/SimpleTable';
import ConfirmDeleteModal from '../../Modals/ConfirmDeleteModal';
import useAlerts from '../../../utils/hooks/useAlerts';
import NewTreeRoot from './NewTreeRootDialog';
import useBoolean from '../../../utils/hooks/useBoolean';
import ContentPage from '../../ContentView/ContentPage/ContentPage';
import { useTreeRoots, treeKeys } from 'components/GraphTree/queries';

const columns = [
  {
    label: '',
    path: 'displayName',
    isSortable: true,
    searchItem: true
  }
];

const DefaultSortBy = {
  path: 'displayName',
  direction: SortDirection.DESC
};


const AdminTreesPage = ({ breadcrumbs }) => {
  const history = useHistory();
  const api = useAPI();
  const T = useTranslation();
  const queryClient = useQueryClient();

  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [alerts, addSuccessAlert, addErrorAlert] = useAlerts();
  const [newTreeDialogOpen, showNewTreeDialog, hideNewTreeDialog] = useBoolean();

  const [treeRoots, isLoading,, hasError] = useTreeRoots();


  const deleteTreeRoot = useMutation({
    mutationFn: createMutationRequest(api.core.deleteNode),
    onMutate: (tree) => {
      const oldData = queryClient.getQueryData(treeKeys.trees());
      queryClient.setQueryData(treeKeys.trees(), (old) => {
        return _.reject(old, { id: tree.id });
      });
      return oldData;
    },
    onError: (err, variables, oldData) => {
      console.error('deleteTreeRoot', err);
      console.error('deleteTreeRoot', variables);
      addErrorAlert(err);
      queryClient.setQueryData(treeKeys.trees(), oldData);
    },
    onSettled: () => {
      queryClient.invalidateQueries(treeKeys.trees());
    },
    onSuccess: () => {
      setConfirmDeleteItem(null);
      addSuccessAlert(T.common.request.delete.success);
    }
  });


  const onDeleteConfirm = useCallback(() => {
    deleteTreeRoot.mutate(confirmDeleteItem.id);
  }, [deleteTreeRoot, confirmDeleteItem]);

  const onRowClick = useCallback((tree) => {
    history.push('/admin/trees/' + tree.id);
  }, [history]);

  const createTree = useCallback(() => {
    showNewTreeDialog();
  }, [showNewTreeDialog]);

  const onDelete = useCallback(tree => setConfirmDeleteItem(tree), []);

  const cancelDelete = useCallback(() => setConfirmDeleteItem(null), []);

  const actions = useMemo(() => createDefaultActions({ onDelete }), [onDelete]);

  const onNewTreeSave = useCallback((newTree) => {
    hideNewTreeDialog();
    history.push('/admin/trees/' + newTree.id);
  }, [hideNewTreeDialog, history]);


  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/trees/list'}>
      { alerts }
      <SimpleTable
        actions={actions}
        isLoading={isLoading && _.isEmpty(treeRoots)}
        onRowClick={onRowClick}
        data={treeRoots}
        columns={columns}
        globalActions={[{ onClick: createTree }]}
        sortBy={DefaultSortBy}
        hasError={hasError}
        errorText={T.admin.trees.error.failedload}
        compact
        usePaging
      />
      <ConfirmDeleteModal
        isLoading={deleteTreeRoot.isLoading}
        onConfirm={onDeleteConfirm}
        item={confirmDeleteItem}
        itemName={confirmDeleteItem?.displayName ?? T.common.noname}
        onCancel={cancelDelete}
      />
      <NewTreeRoot
        isOpen={newTreeDialogOpen}
        onSave={onNewTreeSave}
        onCancel={() => hideNewTreeDialog()}
      />
    </ContentPage>
  );
};

export default AdminTreesPage;
